/**
 * Responsibility: Pure combat UI state derivation and formatting logic.
 * Scope: Owns transformation of GameState/CombatState into UI-ready payloads.
 */
import type { GameState, CombatState, CombatFoeState, Vessel, NPC, ActionOption, CombatLogEntry } from "@anyquest/shared-core"
import { CombatStatus } from "@anyquest/shared-core"
import { formatVitalValue } from './vitals'

export interface CombatUiState {
  npcName: string
  npcVitality: number
  foes: { name: string; vitality: number }[]
  activeTargetName: string
  playerVitality: number
  playerVitalityMax: number
  playerSpirit: number
  playerSpiritMax: number
  playerEssence: number
  playerEssenceMax: number
  round: number
  initiative: string[]
  turnActor: string
  log: CombatLogEntry[]
  readyAbilities: any[]
  combatItems: any[]
  participants: any[]
  allyBars: any[]
  playerLabel: string
}

export function deriveCombatUiState(gs: GameState, currentUiState: any): Partial<CombatUiState> {
  const c = gs.combat
  if (!c) return {}

  const foes = getCombatFoes(c, currentUiState)
  const activeTargetName = resolveActiveTargetName(foes, c.activeTargetName || currentUiState.activeTargetName)
  const activeFoe = foes.find(foe => foe.name === activeTargetName)

  return {
    npcName: activeTargetName || c.npc?.name || '???',
    npcVitality: activeFoe?.vitality ?? c.npcVitality ?? 100,
    foes,
    activeTargetName,
    playerVitality: gs.player.vitals?.vitality ?? 100,
    playerVitalityMax: gs.player.vitals?.maxVitality ?? 100,
    playerSpirit: gs.player.vitals?.spirit ?? 100,
    playerSpiritMax: gs.player.vitals?.maxSpirit ?? 100,
    playerEssence: gs.player.vitals?.essence ?? 100,
    playerEssenceMax: gs.player.vitals?.maxEssence ?? 100,
    playerLabel: getPlayerLabel(gs),
    round: c.round,
    initiative: c.initiative ?? [],
    turnActor: peekTurnActor(c),
    log: c.log?.slice(-8) ?? [],
    readyAbilities: getReadyAbilities(gs),
    combatItems: getCombatItems(gs),
    participants: buildParticipants(gs, foes),
    allyBars: buildAllyBars(gs),
  }
}

export function getPlayerLabel(gs: GameState): string {
  const raw = String(gs?.player?.name ?? gs?.player?.identity ?? '').trim()
  if (!raw) return 'YOU'
  const short = raw.split(/\s+/).slice(0, 2).join(' ').toUpperCase()
  return `${short} (YOU)`
}

export function getCombatItems(gs: GameState) {
  const inventory = Array.isArray(gs?.player?.inventory) ? gs.player.inventory : []
  return inventory
    .map(item => ({
      name: String(item?.name ?? '').trim(),
      equipped: !!item?.equipped,
      desc: item?.desc,
      actions: Array.isArray(item?.actions) ? item.actions : undefined,
    }))
    .filter(item => !!item.name)
    .sort((a, b) => Number(b.equipped) - Number(a.equipped))
    .slice(0, 6)
}

export function buildAllyBars(gs: GameState) {
  return (gs?.companions ?? []).map(c => ({
    name: (c.name || 'ALLY').split(' ')[0].toUpperCase(),
    vitality: c.vitals?.vitality ?? 100,
    maxVitality: c.vitals?.maxVitality ?? c.vitals?.vitality ?? 100,
    spirit: c.vitals?.spirit ?? 100,
    maxSpirit: c.vitals?.maxSpirit ?? c.vitals?.spirit ?? 100,
    essence: c.vitals?.essence ?? 100,
    maxEssence: c.vitals?.maxEssence ?? c.vitals?.essence ?? 100,
  }))
}

export function getReadyAbilities(gs: GameState) {
  if (!gs) return []
  const totalMin = gs.world.clock.day * 24 * 60 + gs.world.clock.hour * 60 + gs.world.clock.minute
  return (gs.player.abilities ?? []).filter(a => {
    if (a.lastUsed == null) return true
    return totalMin - a.lastUsed >= a.cooldown
  })
}

export function getCombatFoes(combat: CombatState | undefined, currentUiState: any) {
  const raw = Array.isArray(combat?.foes) ? combat.foes : []
  if (raw.length) {
    return raw
      .map(foe => ({
        name: String(foe?.name ?? '').trim(),
        vitality: Number.isFinite(foe?.vitality) ? Math.max(0, Math.min(100, Number(foe.vitality))) : 0,
      }))
      .filter(foe => !!foe.name)
  }
  const name = String(combat?.npc?.name ?? currentUiState.npcName ?? '').trim()
  if (!name) return []
  const vitality = Number.isFinite(combat?.npcVitality) ? Number(combat?.npcVitality) : currentUiState.npcVitality
  return [{ name, vitality: Math.max(0, Math.min(100, vitality)) }]
}

export function resolveActiveTargetName(foes: { name: string; vitality: number }[], preferred: string | undefined) {
  const preferredName = String(preferred ?? '').trim().toLowerCase()
  if (preferredName) {
    const match = foes.find(foe => foe.name.toLowerCase() === preferredName && foe.vitality > 0)
    if (match) return match.name
  }
  return foes.find(foe => foe.vitality > 0)?.name || foes[0]?.name || ''
}

export function buildParticipants(gs: GameState, foes: { name: string; vitality: number }[]) {
  if (!gs) return []
  const list = []
  const playerEffects = Object.values(gs.player?.modifiers ?? {}).flat().map(mod => mod?.label).filter(Boolean)
  list.push({
    id: 'player',
    name: getPlayerLabel(gs),
    side: 'player',
    sideLabel: 'PLAYER',
    vitals: {
      vitality: formatVitalValue(gs.player.vitals ?? {}, 'vitality'),
      spirit: formatVitalValue(gs.player.vitals ?? {}, 'spirit'),
      essence: formatVitalValue(gs.player.vitals ?? {}, 'essence'),
    },
    hoverDetail: `${gs.player.identity || 'Player'}\nInventory: ${gs.player.inventory?.length ?? 0} items\nEffects: ${playerEffects.join(', ') || 'none'}`,
    detail: [
      `Identity: ${gs.player.identity || 'Unknown'}`,
      `Vitals: VIT ${formatVitalValue(gs.player.vitals ?? {}, 'vitality')} · SPR ${formatVitalValue(gs.player.vitals ?? {}, 'spirit')} · ESS ${formatVitalValue(gs.player.vitals ?? {}, 'essence')}`,
      `Inventory: ${(gs.player.inventory ?? []).map(i => i.equipped ? `${i.name}[E]` : i.name).join(', ') || 'none'}`,
      `Abilities: ${(gs.player.abilities ?? []).map(a => a.name).join(', ') || 'none'}`,
      `Active effects: ${playerEffects.join(', ') || 'none'}`,
    ].join('\n'),
  })

  for (const companion of (gs.companions ?? [])) {
    const fx = Object.values(companion.modifiers ?? {}).flat().map(mod => mod?.label).filter(Boolean)
    list.push({
      id: `ally:${companion.name}`,
      name: companion.name.toUpperCase(),
      side: 'ally',
      sideLabel: 'ALLY',
      vitals: {
        vitality: formatVitalValue(companion.vitals ?? {}, 'vitality'),
        spirit: formatVitalValue(companion.vitals ?? {}, 'spirit'),
        essence: formatVitalValue(companion.vitals ?? {}, 'essence'),
      },
      hoverDetail: `${companion.identity || 'Companion'}\nInventory: ${(companion.inventory ?? []).map(i => i.name).join(', ') || 'none'}`,
      detail: [
        `Identity: ${companion.identity || 'Companion'}`,
        `Vitals: VIT ${formatVitalValue(companion.vitals ?? {}, 'vitality')} · SPR ${formatVitalValue(companion.vitals ?? {}, 'spirit')} · ESS ${formatVitalValue(companion.vitals ?? {}, 'essence')}`,
        `Inventory: ${(companion.inventory ?? []).map(i => i.equipped ? `${i.name}[E]` : i.name).join(', ') || 'none'}`,
        `Abilities: ${(companion.abilities ?? []).map(a => a.name).join(', ') || 'none'}`,
        `Active effects: ${fx.join(', ') || 'none'}`,
      ].join('\n'),
    })
  }

  for (const foe of (foes ?? [])) {
    const npc = (gs.currentRoom?.npcs ?? []).find(n => n.name.toLowerCase() === foe.name.toLowerCase())
    list.push({
      id: `foe:${foe.name}`,
      name: foe.name.toUpperCase(),
      side: 'foe',
      sideLabel: 'FOE',
      vitals: {
        vitality: `${foe.vitality}%`,
        spirit: '??',
        essence: '??',
      },
      hoverDetail: `${npc?.identity || 'Hostile'}\nKnown inventory: ${(npc?.inventory ?? []).join(', ') || 'unknown'}\nKnown effects: ${(npc?.activeEffects ?? []).join(', ') || 'none'}`,
      detail: [
        `Identity: ${npc?.identity || 'Unknown foe'}`,
        `Vitals: VIT ${foe.vitality}% · SPR unknown · ESS unknown`,
        `Relationship: ${typeof npc?.relationship === 'number' ? npc.relationship : 'unknown'}`,
        `Known inventory: ${(npc?.inventory ?? []).join(', ') || 'unknown'}`,
        `Known effects: ${(npc?.activeEffects ?? []).join(', ') || 'none'}`,
        `Lore: ${npc?.lore || 'No intel available.'}`,
      ].join('\n'),
    })
  }

  return list
}

export function buildImpactSummary(turnData: any, playerVitalityDelta: number, npcVitalityDelta: number, activeTargetName: string, npcName: string) {
  const actor = turnData?.actor || 'npc'
  const actorName = String(turnData?.actorName || '').trim()
  const targetName = (activeTargetName || npcName || 'ENEMY').toUpperCase()
  const outcome = String(turnData?.outcome || '').trim()
  const chunks = []
  let tone = 'neutral'
  if (actor === 'npc') {
    const foeLabel = (actorName || npcName || 'ENEMY').toUpperCase()
    const hp = Number.isFinite(turnData?.vitalityDelta) ? turnData.vitalityDelta : playerVitalityDelta
    const sp = Number.isFinite(turnData?.spiritDelta) ? turnData.spiritDelta : 0
    const es = Number.isFinite(turnData?.essenceDelta) ? turnData.essenceDelta : 0
    if (hp < 0) chunks.push(`YOU -${Math.abs(hp)} HP`)
    if (sp < 0) chunks.push(`YOU -${Math.abs(sp)} SP`)
    if (es < 0) chunks.push(`YOU -${Math.abs(es)} ES`)
    if (chunks.length) tone = 'danger'
    else chunks.push('NO DIRECT DAMAGE')
    if (outcome) chunks.push(outcome)
    return { label: `${foeLabel} TURN`, text: chunks.join(' · '), tone }
  }

  if (actor === 'companion') {
    const allyLabel = (actorName || 'ALLY').toUpperCase()
    if (npcVitalityDelta < 0) {
      chunks.push(`${targetName} -${Math.abs(npcVitalityDelta)} HP`)
      tone = 'success'
    } else {
      chunks.push(`${targetName} UNAFFECTED`)
    }
    if (outcome) chunks.push(outcome)
    return { label: `${allyLabel} TURN`, text: chunks.join(' · '), tone }
  }

  if (npcVitalityDelta < 0) {
    chunks.push(`${targetName} -${Math.abs(npcVitalityDelta)} HP`)
    tone = 'success'
  } else {
    chunks.push(`${targetName} UNAFFECTED`)
  }
  if (outcome) chunks.push(outcome)
  return { label: 'YOUR TURN', text: chunks.join(' · '), tone }
}

export function peekTurnActor(combat: CombatState | undefined) {
  const queue = combat?.turnQueue?.length ? combat.turnQueue : combat?.initiative
  if (!Array.isArray(queue) || !queue.length) return ''
  const cursor = Number.isFinite(combat?.turnCursor) ? Math.max(0, Number(combat?.turnCursor)) : 0
  return String(queue[cursor % queue.length] ?? '').trim()
}
